package com.heelion.farmafreelance.agent

import com.heelion.farmafreelance.models.MatchStato
import com.heelion.farmafreelance.models.Professione
import com.heelion.farmafreelance.models.UserRole
import com.heelion.farmafreelance.models.isProfessionista

/** Frasi da conversazione, non domande sui dati né comandi: Farmy risponde come un amico. */
enum class TipoChiacchiera { SALUTO, CONGEDO, GRAZIE, COMPLIMENTO, COME_STAI, CHI_SEI, SERVE_AIUTO, FRUSTRAZIONE }

/**
 * Il lato "amichevole" di Farmy: saluti, grazie, "non so cosa chiederti", e le domande pronte da
 * toccare (all'apertura, dopo una risposta e quando non ha capito), così chi usa l'app non deve
 * indovinare le frasi giuste. Tutto a regole, senza rete.
 */
object ConversazioneFarmy {
    private val SALUTO = Regex("^(ciao|salve|buongiorno|buonasera|buon pomeriggio|hey|ehi|ei|eccomi)( farmy)?$")
    private val CONGEDO = Regex("^(ciao ciao|a dopo|a presto|arrivederci|buonanotte|alla prossima|ci sentiamo)( farmy)?$")
    // Niente "ok grazie": nelle pagine vale come conferma del comando a voce.
    private val GRAZIE = Regex("^(grazie|ti ringrazio|grazie mille|grazie tante|gentilissimo|gentilissima)( farmy)?$")
    private val COMPLIMENTO = Regex("\\b(sei|che) (bravo|brava|forte|grande|un mito|gentile|simpatico|simpatica|utile)\\b|^bravo( farmy)?$|^ottimo lavoro")
    private val COME_STAI = Regex("^(ciao |ehi )?(come stai|come va|tutto bene|come te la passi)( farmy)?$")
    private val CHI_SEI = Regex("^(ma )?(chi sei|tu chi sei|come ti chiami|cosa sei|che cosa sei)( tu)?$")
    private val SERVE_AIUTO = Regex(
        "^(aiutami|mi aiuti|puoi aiutarmi|ho bisogno di (aiuto|una mano)|dammi una mano|non so (cosa|che cosa) (fare|chiederti|chiedere)" +
            "|cosa (posso|ti posso) chiederti|che (domande|cosa) (posso|ti posso) (fare|chiedere)|da dove (comincio|inizio|parto)" +
            "|come funziona( l app| questa app| farmafreelance)?|non so da dove (cominciare|iniziare|partire))$"
    )
    private val FRUSTRAZIONE = Regex(
        "\\bnon (mi )?capisci (niente|nulla|mai)\\b|\\bnon capisci\\b|\\bsei (stupido|inutile|scemo|lento)\\b|\\bnon servi a (niente|nulla)\\b|\\bnon funzioni\\b"
    )

    /**
     * "ciao Farmy, quando lavoro? grazie": il saluto davanti, "per favore" e il "grazie" finale non
     * aiutano a capire la domanda. Si tolgono dalla frase originale, così orari ("8:00") e date restano intatti.
     */
    private val SALUTO_INIZIALE = Regex("^\\s*(ciao|salve|buongiorno|buonasera|hey|ehi)(\\s+farmy)?[\\s,!.]+", RegexOption.IGNORE_CASE)
    private val CORTESIA = Regex("[,\\s]*\\b(per favore|per piacere|perfavore|cortesemente)\\b[,!.]?", RegexOption.IGNORE_CASE)
    private val GRAZIE_FINALE = Regex("[,\\s]+(grazie|grazie mille)[!.\\s]*$", RegexOption.IGNORE_CASE)

    fun tipo(testo: String): TipoChiacchiera? {
        val t = IntentClassifier.normalizza(testo)
        return when {
            t.isEmpty() -> null
            CONGEDO.matches(t) -> TipoChiacchiera.CONGEDO
            SALUTO.matches(t) -> TipoChiacchiera.SALUTO
            GRAZIE.matches(t) -> TipoChiacchiera.GRAZIE
            COME_STAI.matches(t) -> TipoChiacchiera.COME_STAI
            CHI_SEI.matches(t) -> TipoChiacchiera.CHI_SEI
            SERVE_AIUTO.matches(t) -> TipoChiacchiera.SERVE_AIUTO
            FRUSTRAZIONE.containsMatchIn(t) -> TipoChiacchiera.FRUSTRAZIONE
            COMPLIMENTO.containsMatchIn(t) -> TipoChiacchiera.COMPLIMENTO
            else -> null
        }
    }

    /** La frase senza saluto iniziale e senza "per favore"; uguale se non c'è niente da togliere. */
    fun pulisci(testo: String): String {
        val pulito = GRAZIE_FINALE.replace(CORTESIA.replace(SALUTO_INIZIALE.replace(testo, ""), " "), "")
            .replace(Regex("\\s+"), " ").trim()
        return pulito.ifEmpty { testo }
    }

    fun risposta(tipo: TipoChiacchiera): String = when (tipo) {
        TipoChiacchiera.SALUTO -> "Ciao! Che bello sentirti. Dimmi pure con parole tue cosa ti serve, oppure tocca una delle domande qui sotto."
        TipoChiacchiera.CONGEDO -> "A presto! Quando ti serve qualcosa, tocca Farmy e ci sono."
        TipoChiacchiera.GRAZIE -> "Figurati, è un piacere! Se ti serve altro sono qui."
        TipoChiacchiera.COMPLIMENTO -> "Grazie, sei gentile! Io ce la metto tutta per darti una mano."
        TipoChiacchiera.COME_STAI -> "Tutto bene, grazie! E tu? Se vuoi, diamo un'occhiata insieme ai tuoi turni."
        TipoChiacchiera.CHI_SEI -> "Sono Farmy, l'assistente di FarmaFreelance. Ti aiuto a trovare turni, tenere d'occhio agenda " +
            "e pagamenti e usare l'app. Parlami come vuoi: se non capisco, ti propongo io qualche domanda."
        TipoChiacchiera.SERVE_AIUTO -> "Certo, ci sono io! Dimmi con parole tue cosa ti serve. Se non sai da dove partire, " +
            "ecco qualche domanda che puoi farmi: tocca quella che ti interessa."
        TipoChiacchiera.FRUSTRAZIONE -> "Hai ragione, scusami: a volte non capisco al primo colpo. Prova a dirmelo con parole " +
            "semplici, oppure tocca una di queste domande e ci penso io."
    }

    // --- Domande pronte da toccare ---

    // Farmacisti e infermieri lavorano a turno nelle farmacie: per Farmy fanno le stesse domande.
    private val PER_PROFESSIONISTI = setOf(UserRole.FARMACISTA, UserRole.INFERMIERE)
    private val PER_TUTTI = setOf(UserRole.FARMACISTA, UserRole.INFERMIERE, UserRole.FARMACIA)

    /** Una domanda di esempio per ogni intento del modello (e per chi ha senso). */
    private val ESEMPI: Map<String, Pair<String, Set<UserRole>>> = mapOf(
        "AIUTO" to ("Cosa sai fare?" to PER_TUTTI),
        "DOMANDA_PROSSIMO_TURNO" to ("Quando è il mio prossimo turno?" to PER_TUTTI),
        "DOMANDA_STATO_TURNI" to ("Ho nuove proposte?" to PER_TUTTI),
        "RISPONDI_PROPOSTA" to ("Ho nuove proposte?" to PER_TUTTI),
        "DOMANDA_NOTIFICHE" to ("Ho notifiche?" to PER_TUTTI),
        "DOMANDA_ABBONAMENTO" to (DOMANDA_ABBONAMENTO to PER_TUTTI),
        "DOMANDA_REPUTAZIONE" to ("Quante stelle ho?" to PER_TUTTI),
        "DOMANDA_RIEPILOGO" to ("Cosa devo fare oggi?" to PER_TUTTI),
        "DOMANDA_AGENDA" to ("Fammi il riepilogo dei miei turni" to PER_TUTTI),
        "GESTISCI_ANNUNCI_CANCELLA" to ("Fammi il riepilogo dei miei turni" to PER_TUTTI),
        "GESTISCI_ANNUNCI_MODIFICA" to ("Fammi il riepilogo dei miei turni" to PER_TUTTI),
        "DOMANDA_TURNI_DISPONIBILI" to ("Ci sono turni nuovi?" to PER_TUTTI),
        "DOMANDA_TURNI_PREFERITI" to ("Ci sono turni dei miei preferiti?" to PER_TUTTI),
        "PREFERITI" to ("Ci sono turni dei miei preferiti?" to PER_TUTTI),
        "DOMANDA_EMERGENZE" to ("Ci sono emergenze vicino a me?" to PER_TUTTI),
        "DOMANDA_PROMEMORIA" to ("Quali promemoria ho?" to PER_TUTTI),
        "PROMEMORIA_IMPOSTA" to ("Ricordami ogni mattina i turni della giornata" to PER_TUTTI),
        "CREA_TURNO" to ("Crea un turno domani dalle 8 alle 20" to PER_TUTTI),
        "DOMANDA_GUADAGNI" to ("Quanto ho guadagnato questo mese?" to PER_PROFESSIONISTI),
        "DOMANDA_FARMACIE_PAGANTI" to ("Quali farmacie mi hanno pagato di più?" to PER_PROFESSIONISTI),
        "DOMANDA_TURNI_PAGATI" to ("Chi paga di più vicino a me?" to PER_PROFESSIONISTI),
        "CERCA_FARMACIE" to (TURNI_VICINI to PER_PROFESSIONISTI),
        "PRENDI_TURNO" to (TURNI_VICINI to PER_PROFESSIONISTI),
        "CERCA_FARMACISTI" to ("Cerca farmacisti a Milano" to setOf(UserRole.FARMACIA))
    )

    /** Parole che fanno capire l'argomento anche quando la frase non si capisce tutta. */
    private val ARGOMENTI: List<Triple<Regex, String, Set<UserRole>>> = listOf(
        Triple(Regex("\\b(pag\\w*|soldi|incass\\w*|fattur\\w*|bonific\\w*)\\b"), "Chi mi deve ancora pagare?", PER_PROFESSIONISTI),
        Triple(Regex("\\b(guadagn\\w*|euro|entrat\\w*|stipendi\\w*)\\b"), "Quanto ho guadagnato questo mese?", PER_PROFESSIONISTI),
        Triple(Regex("\\b(vicin\\w*|zona|distan\\w*|km|dove)\\b"), TURNI_VICINI, PER_PROFESSIONISTI),
        Triple(Regex("\\b(urgent\\w*|subito|stasera|emergenz\\w*)\\b"), "Ci sono emergenze vicino a me?", PER_TUTTI),
        Triple(Regex("\\b(propost\\w*|invit\\w*|richiest\\w*)\\b"), "Ho nuove proposte?", PER_TUTTI),
        Triple(Regex("\\b(agenda|calendario|impegn\\w*|settimana)\\b"), "Fammi il riepilogo dei miei turni", PER_TUTTI),
        Triple(Regex("\\b(ricord\\w*|promemoria|sveglia)\\b"), "Ricordami ogni mattina i turni della giornata", PER_TUTTI),
        Triple(Regex("\\b(abbonament\\w*|scade\\w*|rinnov\\w*)\\b"), DOMANDA_ABBONAMENTO, PER_TUTTI),
        Triple(Regex("\\b(stell\\w*|voto|voti|reputazion\\w*)\\b"), "Quante stelle ho?", PER_TUTTI),
        Triple(Regex("\\b(turn\\w*|lavor\\w*)\\b"), "Quando è il mio prossimo turno?", PER_TUTTI)
    )

    private const val TURNI_VICINI = "Turni nelle farmacie vicino a me"
    private const val DOMANDA_ABBONAMENTO = "Quando scade l'abbonamento?"

    /** Per la farmacia nello spazio infermieri: le ricerche di infermieri, con zona, periodo e servizio. */
    private val RICERCHE_INFERMIERI = listOf(
        "Cercami un infermiere in zona per novembre",
        "Mi serve un infermiere per i prelievi sabato",
        "Ci sono infermieri liberi la prossima settimana?"
    )

    private fun perChi(ruolo: UserRole?, ruoli: Set<UserRole>) = ruolo == null || ruolo in ruoli

    /**
     * Le domande di partenza adatte a chi usa l'app (farmacista, infermiere o farmacia); per la farmacia
     * nello spazio infermieri ([professioneCercata]) le ricerche di infermieri.
     */
    private fun predefinite(ruolo: UserRole?, professioneCercata: Professione? = null): List<String> = when {
        ruolo == UserRole.FARMACIA && professioneCercata == Professione.INFERMIERE -> RICERCHE_INFERMIERI
        ruolo == UserRole.FARMACISTA || ruolo == UserRole.INFERMIERE ->
            listOf(TURNI_VICINI, "Quando è il mio prossimo turno?", "Chi mi deve ancora pagare?")
        ruolo == UserRole.FARMACIA -> listOf("Crea un turno domani dalle 8 alle 20", "Ho nuove proposte?", "Cosa devo fare oggi?")
        else -> listOf("Cosa sai fare?")
    }

    /**
     * Quando non ha capito: prima le ipotesi del modello (anche se non abbastanza sicure per agire),
     * poi le parole chiave della frase, poi le domande di partenza. Al massimo 3, senza doppioni.
     */
    fun suggerimentiNonCapito(
        testo: String, ipotesi: List<String>, ruolo: UserRole?, professioneCercata: Professione? = null,
        abbonamentiAttivi: Boolean = true
    ): List<String> {
        val t = IntentClassifier.normalizza(testo)
        val infermieri = ruolo == UserRole.FARMACIA &&
            (professioneCercata == Professione.INFERMIERE || t.contains("infermier"))
        // La farmacia che cerca infermieri: "cerca farmacisti" diventa la ricerca di infermieri.
        val dalModello = ipotesi.mapNotNull { label ->
            if (infermieri && label == "CERCA_FARMACISTI") RICERCHE_INFERMIERI.first()
            else ESEMPI[label]?.takeIf { perChi(ruolo, it.second) }?.first
        }
        val dalleParole = ARGOMENTI.filter { (re, _, ruoli) -> re.containsMatchIn(t) && perChi(ruolo, ruoli) }.map { it.second }
        val partenza = predefinite(ruolo, if (infermieri) Professione.INFERMIERE else professioneCercata)
        // Finché l'app è gratuita non si propone la domanda sull'abbonamento.
        return (dalModello + dalleParole + partenza).distinct().filter { abbonamentiAttivi || it != DOMANDA_ABBONAMENTO }.take(3)
    }

    /** Il messaggio quando non ha capito: mai un secco "non ho capito". */
    fun nonCapito(dalModello: Boolean): String =
        if (dalModello) "Non sono sicuro di aver capito bene. Intendevi una di queste? Toccala, oppure dimmelo con altre parole."
        else "Scusami, questa non l'ho capita. Prova a dirmelo con altre parole, oppure tocca una di queste domande."

    /** All'apertura di Farmy: prima quello che conta adesso (proposte da guardare, impegni vicini), poi le solite. */
    fun iniziali(ctx: ContestoUtente, impegniVicini: Boolean): List<String> {
        val urgenti = buildList {
            if (ctx.matches.any { it.stato == MatchStato.PROPOSTO }) add("Ho nuove proposte?")
            if (impegniVicini) add("Cosa devo fare oggi?")
        }
        return (urgenti + predefinite(ctx.ruolo, ctx.professioneCercata)).distinct().take(3)
    }

    /** Dopo una risposta: la domanda che di solito viene dopo, per continuare il discorso. */
    fun seguito(tipo: TipoDomanda, ruolo: UserRole?, professioneCercata: Professione? = null): List<String> {
        val professionista = ruolo?.isProfessionista == true
        val dopo = when (tipo) {
            TipoDomanda.TURNI_VICINI -> if (professionista) listOf("Chi paga di più vicino a me?", "Ci sono emergenze vicino a me?") else emptyList()
            TipoDomanda.TURNI_PIU_PAGATI -> if (professionista) listOf(TURNI_VICINI) else emptyList()
            TipoDomanda.EMERGENZE -> if (professionista) listOf(TURNI_VICINI) else emptyList()
            TipoDomanda.PROSSIMO_TURNO, TipoDomanda.RIEPILOGO -> listOf("Fammi il riepilogo dei miei turni")
            TipoDomanda.AGENDA -> if (professionista) listOf("Chi mi deve ancora pagare?", "Quanto ho guadagnato questo mese?") else emptyList()
            TipoDomanda.GUADAGNI -> if (professionista) listOf("Chi mi deve ancora pagare?", "Quali farmacie mi hanno pagato di più?") else emptyList()
            TipoDomanda.PAGAMENTI -> if (professionista) listOf("Quali farmacie mi hanno pagato di più?") else emptyList()
            TipoDomanda.FARMACIE_PIU_PAGANTI -> if (professionista) listOf("Chi paga di più vicino a me?") else emptyList()
            TipoDomanda.STATO_TURNI -> listOf("Quando è il mio prossimo turno?")
            TipoDomanda.AIUTO -> predefinite(ruolo, professioneCercata)
            else -> emptyList()
        }
        return dopo.take(2)
    }
}
